'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CloudArrowUp as CloudArrowUpIcon, MapPin as MapPinIcon , FileText as FileTextIcon } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react/dist/lib/types';
import { Bank as BankIcon } from '@phosphor-icons/react/dist/ssr/Bank';
import { Bell as BellIcon } from '@phosphor-icons/react/dist/ssr/Bell';
import { Buildings as BuildingsIcon } from '@phosphor-icons/react/dist/ssr/Buildings';
import { CalendarCheck as CalendarCheckIcon } from '@phosphor-icons/react/dist/ssr/CalendarCheck';
import { CreditCard as CreditCardIcon } from '@phosphor-icons/react/dist/ssr/CreditCard';
import { House as HouseIcon } from '@phosphor-icons/react/dist/ssr/House';
import { Info as InfoIcon } from '@phosphor-icons/react/dist/ssr/Info';
import { LockKey as LockKeyIcon } from '@phosphor-icons/react/dist/ssr/LockKey';
import { PaperPlaneTilt as PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { PlugsConnected as PlugsConnectedIcon } from '@phosphor-icons/react/dist/ssr/PlugsConnected';
import { UserCircle as UserCircleIcon } from '@phosphor-icons/react/dist/ssr/UserCircle';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react/dist/ssr/UsersThree';

import type { NavItemConfig } from '@/types/v2/nav';
import { paths } from '@/paths';
import { isNavItemActive } from '@/lib/v2/is-nav-item-active';
import { usePathname } from '@/hooks/v2/use-pathname';
import { RouterLink } from '@/components/core/v2/link';
import { useFeatureFlags } from '@/hooks/use-feature-flags';

const icons = {
  'credit-card': CreditCardIcon,
  'lock-key': LockKeyIcon,
  'plugs-connected': PlugsConnectedIcon,
  'paper-plane-tilt': PaperPlaneTiltIcon,
  'user-circle': UserCircleIcon,
  'users-three': UsersThreeIcon,
  'map-pin': MapPinIcon,
  house: HouseIcon,
  buildings: BuildingsIcon,
  info: InfoIcon,
  bell: BellIcon,
  import: CloudArrowUpIcon,
  activity: FileTextIcon,
  bank: BankIcon,
  'calendar-check': CalendarCheckIcon,
} as Record<string, Icon>;

export function SideNav(): React.JSX.Element {
  const pathname = usePathname();
  // find feature flag for activity log
  const activityLogFeatureFlag = useFeatureFlags();
  const isActivityLogFeatureFlagEnabled = activityLogFeatureFlag.isFeatureEnabled('activity-log');

  const navItems = [
    {
      key: 'administration',
      title: '',
      items: [
        { key: 'details', title: 'Details', href: paths.settings.community.index, icon: 'info' },
        { key: 'import', title: 'Import', href: paths.settings.community.import, icon: 'import' },
        { key: 'amenities', title: 'Amenities', href: paths.settings.community.amenities.index, icon: 'bank' },
        { key: 'reservations', title: 'Reservations', href: paths.settings.community.reservations?.index, icon: 'calendar-check' },
        { key: 'preferences', title: 'Preferences', href: paths.settings.community.preferences, icon: 'info' },
        // hide activity log if feature flag is not enabled
        ...(isActivityLogFeatureFlagEnabled ? [{ key: 'activity', title: 'Activity Log', href: paths.settings.community.activity, icon: 'activity' }] : []),

        // {
        //   key: 'managers',
        //   title: 'Managers',
        //   href: paths.settings.community.managers.index,
        //   icon: 'users-three',
        //   matcher: {
        //     type: 'startsWith',
        //     href: paths.settings.community.managers.index,
        //   },
        // },
      ],
    },
    // {
    //   key: 'organization',
    //   title: 'Organization',
    //   items: [
    //     { key: 'billing', title: 'Billing & plans', href: '/asdf', icon: 'credit-card', disabled: true },
    //     // { key: 'team', title: 'Team', href: '/asdf', icon: 'users-three' },
    //     // {
    //     //   key: 'integrations',
    //     //   title: 'Integrations',
    //     //   href: '/asdf',
    //     //   icon: 'plugs-connected',
    //     // },
    //   ],
    // },
  ] satisfies NavItemConfig[];

  return (
    <div>
      <Stack
        spacing={3}
        sx={{
          flex: '0 0 auto',
          flexDirection: { xs: 'column-reverse', md: 'column' },
          position: { md: 'sticky' },
          top: '64px',
          width: { xs: '100%', md: '240px' },
        }}
      >
        <Stack component="ul" spacing={3} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {navItems.map((group) => (
            <Stack component="li" key={group.key} spacing={2}>
              {group.title ? (
                <div>
                  <Typography color="text.secondary" variant="caption">
                    {group.title}
                  </Typography>
                </div>
              ) : null}
              <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {group.items.map((item) => (
                  <NavItem {...item} key={item.key} pathname={pathname} />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </div>
  );
}

interface NavItemProps extends NavItemConfig {
  pathname: string;
}

function NavItem({ disabled, external, href, icon, matcher, pathname, title }: NavItemProps): React.JSX.Element {
  const active = isNavItemActive({ disabled, external, href, matcher, pathname });
  const Icon = icon ? icons[icon] : null;

  return (
    <Box component="li" sx={{ userSelect: 'none' }}>
      <Box
        {...(href
          ? {
              component: external ? 'a' : RouterLink,
              href,
              target: external ? '_blank' : undefined,
              rel: external ? 'noreferrer' : undefined,
            }
          : { role: 'button' })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--mui-palette-text-secondary)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && { color: 'var(--mui-palette-text-disabled)', cursor: 'not-allowed' }),
          ...(active && { bgcolor: 'var(--mui-palette-action-selected)', color: 'var(--mui-palette-text-primary)' }),
          '&:hover': {
            ...(!active &&
              !disabled && { bgcolor: 'var(--mui-palette-action-hover)', color: 'var(--mui-palette-text-primary)' }),
          },
        }}
        tabIndex={0}
      >
        {Icon ? (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
            <Icon
              fill={active ? 'var(--mui-palette-text-primary)' : 'var(--mui-palette-text-secondary)'}
              fontSize="var(--icon-fontSize-md)"
              weight={active ? 'fill' : undefined}
            />
          </Box>
        ) : null}
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
